import apiClient from "./apiClient";
import { getOrderSaleLineItems } from "./salesOrderReceiptUtils";
import {
  getDefaultTaxConfiguration,
  normalizeTaxConfiguration,
  TAXATION_TYPES,
  isZatcaTaxationType,
  getZatcaPhaseFromTaxConfig,
} from "./taxConfigurationUtils";
import {
  getZatcaB2bCheckoutUrl,
  getZatcaPhase1B2bGenerateUrl,
  getZatcaPhase1B2cGenerateUrl,
  getZatcaPosCheckoutUrl,
} from "../apis/zatca/zatca.api";

export function hasZatcaApp(purchasedApps) {
  return purchasedApps?.some(
    (app) => app.PType === "ZATCA" && app.PStatus === "Active"
  );
}

export function isZatcaConfigured(zatcaMetadata) {
  return Boolean(
    zatcaMetadata?.vatNumber &&
      zatcaMetadata?.crNumber &&
      zatcaMetadata?.name
  );
}

/** ZATCA module purchased, taxation enabled, and type is Phase 1 or Phase 2. */
export function isZatcaTaxModeSelected(purchasedApps, taxConfig) {
  if (!hasZatcaApp(purchasedApps)) return false;

  const config = normalizeTaxConfiguration(taxConfig || getDefaultTaxConfiguration());
  return config.taxationEnabled && isZatcaTaxationType(config.taxationType);
}

export function isZatcaPhase1TaxModeSelected(purchasedApps, taxConfig) {
  if (!hasZatcaApp(purchasedApps)) return false;
  const config = normalizeTaxConfiguration(taxConfig || getDefaultTaxConfiguration());
  return (
    config.taxationEnabled &&
    config.taxationType === TAXATION_TYPES.ZATCA_PHASE_1
  );
}

export function isZatcaPhase2TaxModeSelected(purchasedApps, taxConfig) {
  if (!hasZatcaApp(purchasedApps)) return false;
  const config = normalizeTaxConfiguration(taxConfig || getDefaultTaxConfiguration());
  return (
    config.taxationEnabled &&
    config.taxationType === TAXATION_TYPES.ZATCA_PHASE_2
  );
}

export function getZatcaPhase(taxConfig) {
  return getZatcaPhaseFromTaxConfig(taxConfig);
}

/** Full ZATCA checkout: purchased + tax mode ZATCA + seller credentials configured. */
export function isZatcaActive(purchasedApps, zatcaMetadata, taxConfig) {
  if (!isZatcaTaxModeSelected(purchasedApps, taxConfig)) return false;
  return isZatcaConfigured(zatcaMetadata);
}

export function formatZatcaMetadata(metadata) {
  if (!metadata) return null;

  let vatNumber = metadata.vatNumber || "";
  if (vatNumber.length !== 15) {
    vatNumber =
      vatNumber.length < 15
        ? `3${vatNumber.padEnd(13, "0")}3`
        : vatNumber.substring(0, 15);
  }

  let buildingNumber = metadata.address?.buildingNumber || "";
  if (buildingNumber.length !== 4) {
    buildingNumber =
      buildingNumber.length < 4
        ? buildingNumber.padStart(4, "0")
        : buildingNumber.substring(0, 4);
  }

  return {
    tenantId: metadata.tenantId || metadata.companyName,
    name: metadata.name || "",
    vatNumber,
    crNumber: metadata.crNumber || "",
    address: {
      street: metadata.address?.street || "",
      buildingNumber,
      district: metadata.address?.district || "",
      city: metadata.address?.city || "",
      postalCode: metadata.address?.postalCode || "",
      country: metadata.address?.country || "SA",
    },
  };
}

export function validateZatcaMetadata(metadata) {
  const errors = [];
  if (!metadata?.vatNumber) errors.push("VAT number is required");
  if (!metadata?.crNumber) errors.push("CR Number is required");
  if (!metadata?.name) errors.push("Seller name is required");

  return {
    isValid: errors.length === 0,
    errors,
    metadata: formatZatcaMetadata(metadata),
  };
}

export function normalizeZatcaResponse(response) {
  if (!response || typeof response !== "object") return response;

  const qrCodeData =
    response.qrCodeData ||
    response.qrCode ||
    response.qr ||
    response.qrSellertStatus ||
    response.qrSellerStatus ||
    null;

  return { ...response, qrCodeData };
}

/** Merge duplicate sold lines and exclude returned rows for ZATCA invoice display. */
export function consolidateZatcaLineItems(lineItems = []) {
  const merged = new Map();

  for (const item of lineItems) {
    if (item?.returned) continue;

    const name = item.productName || item.name || "Item";
    const taxRate = Number(item.tax || item.taxRate || 15);
    const unitPrice = parseFloat(item.price || 0) || 0;
    const quantity = parseFloat(item.quantity || item.units || 0) || 0;
    if (quantity <= 0) continue;

    const key = `${item.productId || name}|${unitPrice}|${taxRate}`;

    if (merged.has(key)) {
      merged.get(key).quantity += quantity;
    } else {
      merged.set(key, {
        ...item,
        productName: name,
        name,
        price: unitPrice,
        quantity,
        tax: taxRate,
      });
    }
  }

  return Array.from(merged.values());
}

export function resolveZatcaLineItems(order, lineItems) {
  const source = lineItems?.length
    ? lineItems.filter((item) => !item?.returned)
    : getOrderSaleLineItems(order);
  return consolidateZatcaLineItems(source);
}

export function calculateZatcaTotals(lineItems = []) {
  let totalExcludingVAT = 0;
  let totalVAT = 0;

  const items = lineItems.map((item, index) => {
    let taxRate = Number(item.tax || item.taxRate || 15);
    if (taxRate !== 5 && taxRate !== 15) taxRate = 15;

    const quantity = parseFloat(item.quantity || item.units || 1) || 1;
    const unitPrice = parseFloat(item.price || 0) || 0;
    const itemTotal = unitPrice * quantity;
    const itemVAT = (itemTotal * taxRate) / 100;

    totalExcludingVAT += itemTotal;
    totalVAT += itemVAT;

    return {
      id: index + 1,
      name: item.productName || item.name || `Item ${index + 1}`,
      quantity,
      unitPrice,
      taxRate,
      totalExcludingVAT: Number(itemTotal.toFixed(2)),
    };
  });

  return {
    totalExcludingVAT: Number(totalExcludingVAT.toFixed(2)),
    totalVAT: Number(totalVAT.toFixed(2)),
    totalIncludingVAT: Number((totalExcludingVAT + totalVAT).toFixed(2)),
    items,
  };
}

function mapPosPaymentMethod(order) {
  const method = String(order?.paymentType || order?.paymentMethod || "cash").toLowerCase();
  if (method.includes("card")) return "Card";
  if (method.includes("bank")) return "Bank";
  if (order?.paid === false) return "Credit";
  return "Cash";
}

function mapB2bPaymentMethod(order) {
  return String(order?.paymentType || order?.paymentMethod || "cash").toLowerCase();
}

function mapPhase1PaymentMethod(order) {
  const method = String(order?.paymentType || order?.paymentMethod || "cash").toLowerCase();
  if (method.includes("card")) return "card";
  if (method.includes("bank")) return "bank_transfer";
  return "cash";
}

function resolveBuyerName(order, customer) {
  return (
    customer?.name ||
    customer?.displayName ||
    order?.customerName ||
    order?.customer?.name ||
    order?.customer?.customerName ||
    order?.buyerName ||
    "Customer"
  );
}

export function normalizeVatInput(value) {
  return String(value || "").replace(/\D/g, "");
}

/** Saudi ZATCA VAT: 15 digits, starts with 3 and ends with 3. */
export function validateSaudiVatNumber(vat, { sellerVat, required = true } = {}) {
  const errors = [];
  const normalized = normalizeVatInput(vat);

  if (!normalized) {
    if (required) errors.push("VAT number is required");
    return { isValid: errors.length === 0, errors, normalized };
  }

  if (normalized.length !== 15) {
    errors.push("VAT number must be exactly 15 digits");
  }
  if (!/^3\d{13}3$/.test(normalized)) {
    errors.push("VAT number must start with 3 and end with 3 (Saudi ZATCA format)");
  }

  if (sellerVat) {
    const sellerNorm = normalizeVatInput(sellerVat);
    if (sellerNorm && normalized === sellerNorm) {
      errors.push("Buyer VAT cannot be the same as your company VAT");
    }
  }

  return { isValid: errors.length === 0, errors, normalized };
}

export function resolveB2bBuyer(order, customer, buyerVatNumber = "", zatcaMetadata = null) {
  const buyer = {
    name: resolveBuyerName(order, customer),
  };

  const vat =
    buyerVatNumber ||
    customer?.vatNumber ||
    customer?.taxNumber ||
    order?.customerVatNumber;

  const normalizedVat = normalizeVatInput(vat);
  if (normalizedVat) {
    buyer.vatNumber = normalizedVat;
  }

  const address = resolveZatcaSetupAddress(zatcaMetadata);
  if (address) {
    buyer.address = address;
  }

  return buyer;
}

function resolveZatcaSetupAddress(zatcaMetadata) {
  if (!zatcaMetadata?.address) return null;

  let buildingNumber = zatcaMetadata.address.buildingNumber || "";
  if (buildingNumber.length !== 4) {
    buildingNumber =
      buildingNumber.length < 4
        ? buildingNumber.padStart(4, "0")
        : buildingNumber.substring(0, 4);
  }

  return {
    street: zatcaMetadata.address.street || "",
    buildingNumber,
    district: zatcaMetadata.address.district || "",
    city: zatcaMetadata.address.city || "",
    postalCode: zatcaMetadata.address.postalCode || "",
    country: zatcaMetadata.address.country || "SA",
  };
}

export function validateB2bBuyer(buyer, { sellerVat, requireVat = false, requireAddress = false } = {}) {
  const errors = [];
  if (!buyer?.name?.trim()) errors.push("Buyer name is required");

  if (requireVat || buyer?.vatNumber) {
    const vatValidation = validateSaudiVatNumber(buyer?.vatNumber, {
      sellerVat,
      required: requireVat,
    });
    errors.push(...vatValidation.errors);
  }

  if (requireAddress || buyer?.address) {
    if (!buyer?.address?.street?.trim()) {
      errors.push("Street is required in ZATCA setup address");
    }
    if (!buyer?.address?.buildingNumber?.trim()) {
      errors.push("Building number is required in ZATCA setup address");
    }
    if (!buyer?.address?.district?.trim()) {
      errors.push("District is required in ZATCA setup address");
    }
    if (!buyer?.address?.city?.trim()) {
      errors.push("City is required in ZATCA setup address");
    }
    if (!buyer?.address?.postalCode?.trim()) {
      errors.push("Postal code is required in ZATCA setup address");
    }
    if (!buyer?.address?.country?.trim()) {
      errors.push("Country is required in ZATCA setup address");
    }
  }

  return { isValid: errors.length === 0, errors };
}

export function extractZatcaApiErrors(errorOrResponse) {
  const data = errorOrResponse?.data || errorOrResponse;
  const errorMessages = data?.validationResults?.errorMessages;
  if (Array.isArray(errorMessages) && errorMessages.length) {
    const parsed = errorMessages
      .map((item) => {
        if (item?.message?.includes("BR-CUSTOM-VALIDATION-01")) {
          return "Buyer VAT cannot match your company VAT. Add the customer's own VAT on their profile.";
        }
        if (item?.message?.includes("errorMessages")) {
          try {
            const nested = JSON.parse(
              item.message.match(/\{[\s\S]*\}/)?.[0] || "{}"
            );
            const nestedMsg = nested?.validationResults?.errorMessages?.[0]?.message;
            if (nestedMsg) return nestedMsg;
          } catch {
            // ignore parse errors
          }
        }
        return item.message;
      })
      .filter(Boolean);
    if (parsed.length) return parsed.join(", ");
  }
  return null;
}

export function buildZatcaPosCheckoutPayload({ order, lineItems, invoicePrefix = "PH2-B2C", zatcaPhase = 2 }) {
  const itemsSource = resolveZatcaLineItems(order, lineItems);
  if (!itemsSource.length) {
    throw new Error("No billable items found for ZATCA invoice");
  }

  const totals = calculateZatcaTotals(itemsSource);
  const orderDate = parseOrderDate(order);
  const orderId = order?.orderId || order?.id || Date.now();
  const paddedId = String(orderId).padStart(3, "0");
  const resolvedPrefix =
    invoicePrefix && !/^INV/i.test(String(invoicePrefix))
      ? invoicePrefix
      : zatcaPhase === 1
        ? "PH1-B2C"
        : "PH2-B2C";
  const invoiceNumber = `${resolvedPrefix}-${paddedId}`;
  const posReference = String(orderId);

  return {
    posReference,
    invoiceDetails: {
      invoiceNumber,
      issueDate: orderDate.toISOString().split("T")[0],
      issueTime: orderDate.toTimeString().split(" ")[0],
    },
    buyer: {
      name: resolveBuyerName(order),
    },
    items: totals.items.map(({ id, name, quantity, unitPrice, taxRate }) => ({
      id,
      name,
      quantity,
      unitPrice,
      taxRate,
    })),
    totals: {
      totalExcludingVAT: totals.totalExcludingVAT,
      totalVAT: totals.totalVAT,
      totalIncludingVAT: totals.totalIncludingVAT,
    },
    payment: {
      method:
        zatcaPhase === 1
          ? mapPhase1PaymentMethod(order)
          : mapPosPaymentMethod(order),
    },
  };
}

export function buildZatcaB2bCheckoutPayload({
  order,
  lineItems,
  customer,
  buyerVatNumber = "",
  zatcaMetadata = null,
  invoicePrefix = "PH2-B2B",
  skipBuyerValidation = false,
  zatcaPhase = 2,
}) {
  const itemsSource = resolveZatcaLineItems(order, lineItems);
  if (!itemsSource.length) {
    throw new Error("No billable items found for ZATCA invoice");
  }

  const buyer = resolveB2bBuyer(order, customer, buyerVatNumber, zatcaMetadata);
  if (!skipBuyerValidation) {
    const sellerVat = zatcaMetadata?.vatNumber || zatcaMetadata?.taxNumber;
    const buyerValidation = validateB2bBuyer(buyer, {
      sellerVat,
      requireVat: true,
      requireAddress: true,
    });
    if (!buyerValidation.isValid) {
      throw new Error(buyerValidation.errors.join(", "));
    }
  }

  const totals = calculateZatcaTotals(itemsSource);
  const orderDate = parseOrderDate(order);
  const orderId = order?.orderId || order?.id || Date.now();
  const paddedId = String(orderId).padStart(3, "0");
  const resolvedPrefix =
    invoicePrefix && !/^INV/i.test(String(invoicePrefix))
      ? invoicePrefix
      : zatcaPhase === 1
        ? "PH1-B2B"
        : "PH2-B2B";
  const invoiceNumber = `${resolvedPrefix}-${paddedId}`;

  return {
    invoiceDetails: {
      invoiceNumber,
      issueDate: orderDate.toISOString().split("T")[0],
      issueTime: orderDate.toTimeString().split(" ")[0],
    },
    buyer,
    items: totals.items.map(({ id, name, quantity, unitPrice, taxRate }) => ({
      id,
      name,
      quantity,
      unitPrice,
      taxRate,
    })),
    totals: {
      totalExcludingVAT: totals.totalExcludingVAT,
      totalVAT: totals.totalVAT,
      totalIncludingVAT: totals.totalIncludingVAT,
    },
    payment: {
      method:
        zatcaPhase === 1
          ? mapPhase1PaymentMethod(order)
          : mapB2bPaymentMethod(order),
    },
    reference: String(orderId),
  };
}

export function resolveZatcaCheckoutMode(appType, explicitMode) {
  if (explicitMode === "b2b" || explicitMode === "pos") {
    return explicitMode;
  }
  return appType?.startsWith("HOMS") ? "b2b" : "pos";
}

function resolveZatcaInvoicePrefix({ invoicePrefix, isB2b, zatcaPhase }) {
  const phaseLabel = `PH${zatcaPhase || 2}`;
  const typeSuffix = isB2b ? "B2B" : "B2C";

  if (invoicePrefix && !/^INV/i.test(String(invoicePrefix))) {
    return invoicePrefix;
  }

  return `${phaseLabel}-${typeSuffix}`;
}

function parseOrderDate(order) {
  const raw = order?.createdAt;
  if (typeof raw === "string" && raw.includes(".")) {
    const parts = raw.split(".");
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const hour = parseInt(parts[3], 10) || 0;
    const minute = parseInt(parts[4], 10) || 0;
    const second = parseInt(parts[5], 10) || 0;
    if (Number.isFinite(year)) {
      return new Date(year, month, day, hour, minute, second);
    }
  }

  const parsed = new Date(raw || Date.now());
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

export async function generateZatcaInvoiceFromOrder({
  order,
  lineItems,
  zatcaMetadata,
  invoicePrefix,
  checkoutMode,
  appType,
  customer = null,
  buyerVatNumber = "",
  silent = false,
  zatcaPhase = null,
  taxConfiguration = null,
}) {
  const validation = validateZatcaMetadata(zatcaMetadata);
  if (!validation.isValid) {
    throw new Error(validation.errors.join(", "));
  }

  const tenantId = zatcaMetadata?.tenantId || zatcaMetadata?.companyName;
  if (!tenantId) {
    throw new Error("Tenant ID is required for ZATCA checkout");
  }

  const phase = zatcaPhase ?? getZatcaPhase(taxConfiguration) ?? 2;
  const mode = resolveZatcaCheckoutMode(appType, checkoutMode);
  const isB2b = mode === "b2b";
  const isPhase1 = phase === 1;
  const resolvedInvoicePrefix = resolveZatcaInvoicePrefix({
    invoicePrefix,
    isB2b,
    zatcaPhase: phase,
  });

  const checkoutPayload = isB2b
    ? buildZatcaB2bCheckoutPayload({
        order,
        lineItems,
        customer,
        buyerVatNumber,
        zatcaMetadata,
        invoicePrefix: resolvedInvoicePrefix,
        zatcaPhase: phase,
      })
    : buildZatcaPosCheckoutPayload({
        order,
        lineItems,
        invoicePrefix: resolvedInvoicePrefix,
        zatcaPhase: phase,
      });

  let url;
  if (isPhase1) {
    url = isB2b
      ? getZatcaPhase1B2bGenerateUrl(tenantId)
      : getZatcaPhase1B2cGenerateUrl(tenantId);
  } else {
    url = isB2b
      ? getZatcaB2bCheckoutUrl(tenantId)
      : getZatcaPosCheckoutUrl(tenantId);
  }

  const result = await apiClient.post(url, checkoutPayload, {
    context: isPhase1 ? "ZATCA Phase 1 E-Invoice" : "ZATCA E-Invoice",
    silent,
  });

  if (result?.validationResults?.status === "ERROR") {
    const apiError = extractZatcaApiErrors(result);
    throw new Error(apiError || "ZATCA invoice validation failed");
  }

  const displayTotals = calculateZatcaTotals(
    resolveZatcaLineItems(order, lineItems)
  );

  const billData = {
    ...checkoutPayload,
    items: displayTotals.items,
    totals: checkoutPayload.totals || {
      totalExcludingVAT: displayTotals.totalExcludingVAT,
      totalVAT: displayTotals.totalVAT,
      totalIncludingVAT: displayTotals.totalIncludingVAT,
    },
    invoiceDetails: {
      ...checkoutPayload.invoiceDetails,
      invoiceNumber: result?.invoiceNumber || checkoutPayload.invoiceDetails.invoiceNumber,
    },
    seller: validation.metadata,
  };

  return {
    billData,
    zatcaResponse: normalizeZatcaResponse(result),
  };
}

function extractStoredZatcaResponse(order) {
  if (!order) return null;

  const raw =
    order.zatcaResponse ||
    order.zatca ||
    order.zatcaInvoice ||
    order.zatcaData ||
    null;

  if (raw && typeof raw === "object") {
    return raw;
  }

  const qrCodeData = order.qrCodeData || order.zatcaQrCode || null;
  if (qrCodeData) {
    return {
      qrCodeData,
      validationResults: { status: "PASS" },
    };
  }

  return null;
}

/** Build receipt preview from existing order data — no checkout API call. */
export function buildZatcaReceiptPreviewFromOrder({
  order,
  lineItems,
  zatcaMetadata,
  invoicePrefix,
  checkoutMode,
  appType,
  customer = null,
  buyerVatNumber = "",
  zatcaPhase = null,
  taxConfiguration = null,
}) {
  const validation = validateZatcaMetadata(zatcaMetadata);
  if (!validation.isValid) {
    throw new Error(validation.errors.join(", "));
  }

  const phase = zatcaPhase ?? getZatcaPhase(taxConfiguration) ?? 2;
  const mode = resolveZatcaCheckoutMode(appType, checkoutMode);
  const isB2b = mode === "b2b";
  const resolvedInvoicePrefix = resolveZatcaInvoicePrefix({
    invoicePrefix,
    isB2b,
    zatcaPhase: phase,
  });

  const checkoutPayload = isB2b
    ? buildZatcaB2bCheckoutPayload({
        order,
        lineItems,
        customer,
        buyerVatNumber,
        zatcaMetadata,
        invoicePrefix: resolvedInvoicePrefix,
        skipBuyerValidation: true,
        zatcaPhase: phase,
      })
    : buildZatcaPosCheckoutPayload({
        order,
        lineItems,
        invoicePrefix: resolvedInvoicePrefix,
        zatcaPhase: phase,
      });

  const displayTotals = calculateZatcaTotals(
    resolveZatcaLineItems(order, lineItems)
  );

  const billData = {
    ...checkoutPayload,
    items: displayTotals.items,
    totals: checkoutPayload.totals || {
      totalExcludingVAT: displayTotals.totalExcludingVAT,
      totalVAT: displayTotals.totalVAT,
      totalIncludingVAT: displayTotals.totalIncludingVAT,
    },
    seller: validation.metadata,
  };

  const storedResponse = extractStoredZatcaResponse(order);
  const zatcaResponse = normalizeZatcaResponse(
    storedResponse || {
      validationResults: { status: "PREVIEW" },
      reportingStatus: null,
      clearanceStatus: null,
    }
  );

  return { billData, zatcaResponse };
}

export function formatZatcaMoney(amount) {
  return (Number(amount || 0) / 100).toFixed(2);
}
